import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUserToken, clearAuthData, fetchCurrentUser } from '../stores/sensor/sensorStore';
import useAuth from './useAuth';

const apiUrl = process.env.REACT_APP_BACKEND_URL || '/';
const refreshEndpoint = `${apiUrl.replace(/\/+$/, '')}/refresh`;

const useTokenManager = () => {
  const { setAuth } = useAuth();
  const dispatch = useDispatch();
  const token = useSelector(state => state.sensors.token);

  // Simple token expiration check
  const isTokenExpired = useCallback(token => {
    if (!token) return true;

    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;

      // Token is expired if current time is past expiration
      return tokenPayload.exp < currentTime;
    } catch (error) {
      console.error('Error decoding token:', error);
      return true;
    }
  }, []);

  // Manual refresh function (used by aggressive fetch patch and initialization)
  const refreshToken = useCallback(async () => {
    try {
      const response = await fetch(refreshEndpoint, {
        method: 'GET',
        credentials: 'include',
        headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store',
        'Pragma': 'no-cache',
      },
    });

      if (!response.ok) {
        throw new Error('Manual refresh failed');
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Manual refresh failed');
      }

      const accessToken = data.accessToken;

      // Store in localStorage
      localStorage.setItem('token', accessToken);

      // Update Redux state
      dispatch(setUserToken({ token: accessToken }));

      // Decode token to get user info
      const tokenPayload = JSON.parse(atob(accessToken.split('.')[1]));

      // Update Auth context
      setAuth(prev => ({
        ...prev,
        accessToken: accessToken,
        user: {
          id: tokenPayload.id,
          username: tokenPayload.username,
          role: tokenPayload.role,
          email: tokenPayload.email,
          first_name: tokenPayload.first_name,
          last_name: tokenPayload.last_name,
          phone: tokenPayload.phone,
          buildings: tokenPayload.buildings,
          building_ids: tokenPayload.building_ids,
        },
      }));

      return accessToken;
    } catch (error) {
      console.error('Manual token refresh failed:', error);
      throw error;
    }
  }, [dispatch, setAuth]);

  // Logout function
  const logout = useCallback(() => {
    // Clear all auth state
    localStorage.removeItem('token');
    localStorage.removeItem('persist:root3');
    localStorage.removeItem('currentUser');
    dispatch(clearAuthData());
    setAuth({});

    // Redirect to login
    window.location.href = '/authentication/sign-in';
  }, [dispatch, setAuth]);

  // Initialize auth - simplified for aggressive refresh approach
  const initializeAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('token');

    // No token at all - just return, let App.js show SignIn
    if (!storedToken) {
      return;
    }

    // Token exists but is expired - try to refresh
    if (isTokenExpired(storedToken)) {
      await refreshToken(); // This will throw if refresh token cookie is missing
      const newToken = localStorage.getItem('token');
      dispatch(setUserToken({ token: newToken }));
      dispatch(fetchCurrentUser());
    } else {
      // Token exists and is still valid
      dispatch(setUserToken({ token: storedToken }));
      dispatch(fetchCurrentUser());
    }
  }, [refreshToken, dispatch, isTokenExpired]);

  return {
    token,
    refreshToken, // Manual refresh capability
    logout, // Manual logout
    initializeAuth, // App startup initialization
    isTokenExpired: token ? isTokenExpired(token) : true,
  };
};

export default useTokenManager;
